import React from 'react';
import { Typography, Divider } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { formatToLocalDateTime } from '../../../utils/dateFormatter';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import OutgoingMailIcon from '@mui/icons-material/OutgoingMail';
import PrescriptionPreview from './PrescriptionPreview';
import { sendPrescriptionEmail } from '../../../api/prescriptionService';
import { useSnackbar } from '../../../context/SnackbarContext';
import ConfirmDialog from '../../common/modals/ConfirmDialog';
import RequirePermission from '../../RequirePermission';
import { SUB_MODULES } from '../../../constants/subModulesConstants';

export default function PrescriptionView({handleEdit, open, onClose, prescription = null}) { 
    const { t } = useTranslation('common'); 
    const { showSnackbar } = useSnackbar();
    const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);

    const [previewOpen, setPreviewOpen] = React.useState(false);

    const handleClose = () => {
        onClose();
    };

    const handlePreviewClick = () => {
      setPreviewOpen(true);
    };

    const handleSendEmailClick = async () => {
      try {
        setConfirmDialogOpen(false);
        await sendPrescriptionEmail(prescription.id);
        showSnackbar(t('prescriptions.messages.send_email_success'), "success");
      } catch (error) {
        showSnackbar(t('prescriptions.messages.send_email_fail'), "error");
      }
    };

    const handleEditClick = () => {
        handleEdit(prescription);
    };

    if (!prescription) {
        return (
            <></>
        );
    }

    return (
        <React.Fragment>
        <Dialog onClose={handleClose} open={open} disableRestoreFocus>
            
            <DialogTitle>Prescription</DialogTitle>
            <DialogContent dividers>

                <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Reference Code</Typography>
                <Typography variant="body1" sx={{ mt: 0 }}>{prescription.reference_code}</Typography>

                <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Patient</Typography>
                <Typography variant="body1" sx={{ mt: 0 }}>{`${prescription.pet.name} (${prescription.petOwner.first_name} ${prescription.petOwner.last_name})`}</Typography>

                <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Date</Typography>
                <Typography variant="body1" sx={{ mt: 0 }}>{prescription.date}</Typography>

                <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Valid Until</Typography>
                <Typography variant="body1" sx={{ mt: 0 }}>{prescription.validity_date || "(none)"}</Typography>

                <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Medications and Instructions</Typography>
                <Typography variant="body1" sx={{ mt: 0, whiteSpace: 'pre-line' }}>{prescription.content}</Typography>

                <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>E-mailed to Patient</Typography>
                <Typography variant="body1" sx={{ mt: 0 }}>{prescription.sent ? "Yes" : "No"}</Typography>

                <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Date E-mailed</Typography>
                <Typography variant="body1" sx={{ mt: 0 }}>{prescription.date_sent ? formatToLocalDateTime(prescription.date_sent) : "(none)"}</Typography>

                <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Prescription Disabled</Typography>
                <Typography variant="body1" sx={{ mt: 0 }}>{prescription.disabled ? "Yes" : "No"}</Typography>

                <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Created</Typography>
                <Typography variant="body2" sx={{ mt: 0 }}>{formatToLocalDateTime(prescription.createdAt)}</Typography>

                <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Last Updated</Typography>
                <Typography variant="body2" sx={{ mt: 0 }}>{formatToLocalDateTime(prescription.updatedAt)}</Typography>

                <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Created By</Typography>
                <Typography variant="body2" sx={{ mt: 0 }}>{prescription.user.name}</Typography>

            </DialogContent>
            <DialogActions>
              <RequirePermission subModuleName={SUB_MODULES.PRESCRIPTION} minAccessLevel={2}>
                <Button onClick={handleEditClick}>
                  <EditIcon />
                </Button>
              </RequirePermission>
              <Button onClick={handlePreviewClick}>
                  <FindInPageIcon />
              </Button>
              <Button onClick={() => setConfirmDialogOpen(true)}>
                  <OutgoingMailIcon />
              </Button>
              <div style={{flex: '1 0 0'}} />
              <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
        <PrescriptionPreview 
          prescription={prescription}
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />
        <ConfirmDialog
          open={confirmDialogOpen}
          title="Send Prescription E-mail"
          content={t('prescriptions.messages.send_email_confirm')}
          confirmText="Send E-mail"
          cancelText="Cancel"
          onConfirm={handleSendEmailClick}
          onCancel={() => setConfirmDialogOpen(false)}
        />
        </React.Fragment>
    );
}